import { UUID } from "crypto";

import { EditorAnnotationType, useAnnotationStore } from "@perceptron/core/annotation-editor";
import { MediaKind, DocumentVersion } from "@perceptron/utils/types";
import { JSONContent } from "@tiptap/react";

import { MediaFactory } from "@demo/libs/media-factory";
import { useMediaStore } from "@demo/store/media";
import { Role } from "@demo/types/base";
import { DocumentInputContentItem, DocumentMedia } from "@demo/types/documentContent";
import { BLANK } from "@demo/utils/editor";

import { createMediaSetContainerNode } from "./media-set-factory";

/**
 * Unified function to restore media (images or videos) to the media store
 */
function restoreMediaToStore(
  mediaItems: DocumentMedia[],
  role: "user" | "system",
  documentVersion: DocumentVersion,
  downloadUrls?: Map<UUID, string>,
): string {
  const mediaStore = useMediaStore.getState();
  const annotationStore = useAnnotationStore.getState();
  const roleEnum = role === "user" ? Role.USER : Role.SYSTEM;

  const mediaSetId = mediaStore.addMediaSet(roleEnum, documentVersion);

  // Create MediaData for the media item with URL
  mediaItems.forEach((mediaItem) => {
    const downloadUrl = downloadUrls?.get(mediaItem.value.uuid as UUID);
    if (!downloadUrl) {
      console.error(`No download URL found for media ${mediaItem.value.uuid}`);
    }

    mediaStore.addMedia(mediaSetId, {
      key: mediaItem.value.uuid,
      data: "", // Media uses URLs, not base64 data
      url: downloadUrl,
      extension: mediaItem.value.extension,
    });

    annotationStore.setActiveMedia(mediaSetId, mediaItem.value.uuid);

    // Only images have annotations - use MediaFactory to detect media kind
    const mediaKind = MediaFactory.detectMediaKind({
      extension: mediaItem.value.extension,
      key: mediaItem.value.uuid,
      data: "",
    });

    if (mediaKind === MediaKind.Image && "annotations" in mediaItem.value) {
      const annotations = mediaItem.value.annotations;

      annotations.points.forEach((point: { x: number; y: number; label: string }) => {
        annotationStore.addAnnotation({
          mediaSetId,
          mediaKey: mediaItem.value.uuid,
          x: point.x,
          y: point.y,
          label: point.label,
          type: EditorAnnotationType.Point,
        });
      });

      annotations.boundingBoxes.forEach(
        (box: { x: number; y: number; width: number; height: number; label: string }) => {
          annotationStore.addAnnotation({
            mediaSetId,
            mediaKey: mediaItem.value.uuid,
            x: box.x,
            y: box.y,
            width: box.width,
            height: box.height,
            label: box.label,
            type: EditorAnnotationType.Box,
          });
        },
      );
    }
  });

  return mediaSetId;
}

/**
 * Converts structured DocumentContent array back to TipTap JSON format
 */
export function convertDocumentContentToTipTap(
  content: DocumentInputContentItem[],
  role: "user" | "system" = "user",
  documentVersion: DocumentVersion = DocumentVersion.Draft,
  downloadUrls?: Map<UUID, string>,
): JSONContent {
  const paragraphs: JSONContent[] = [];

  for (const item of content) {
    switch (item.type) {
      case "paragraph":
        // Create separate paragraph for each text item
        if (item.value.type === "text" && item.value.value === "") {
          // For empty text, create empty paragraph (TipTap style)
          paragraphs.push({
            type: "paragraph",
            content: [],
          });
        } else {
          paragraphs.push({
            type: "paragraph",
            content: [
              {
                type: "text",
                text: item.value.value,
              },
            ],
          });
        }
        break;

      case "media":
        const mediaSetId = restoreMediaToStore(item.value, role, documentVersion, downloadUrls);
        const restoredMedia = useMediaStore.getState().mediaSets.find((mediaSet) => mediaSet.id === mediaSetId);
        const restoredMediaIds = restoredMedia?.media.map((media) => media.key) ?? [];
        paragraphs.push(createMediaSetContainerNode(mediaSetId, restoredMediaIds));
        break;
    }
  }

  return {
    type: "doc",
    content: paragraphs.length > 0 ? paragraphs : [{ type: "paragraph", content: [] }],
  };
}

/**
 * Checks if content is already in TipTap format (for backwards compatibility)
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function isTipTapFormat(content: any): content is JSONContent {
  return content && typeof content === "object" && "type" in content && content.type === "doc";
}

/**
 * Safely converts content to TipTap format, handling both structured and TipTap formats
 */
export function ensureTipTapFormat(
  content: DocumentInputContentItem[],
  role: "user" | "system" = "user",
  documentVersion: DocumentVersion = DocumentVersion.Draft,
  downloadUrls: Map<UUID, string>,
): JSONContent {
  if (!content) {
    return BLANK;
  }

  // If it's an array, assume it's structured content
  if (Array.isArray(content)) {
    return convertDocumentContentToTipTap(content, role, documentVersion, downloadUrls);
  }

  // Fallback - return empty document
  return BLANK;
}
